import styled from 'styled-components';
import { useTranslate } from 'react-t7e';
import { FC } from 'react';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import Image from 'next/image';
import { AboutUsSection } from 'src/modules/about-us/components/shared/AboutUsSection';
import { media } from 'src/styles/designs';
import { AboutUsHeading } from 'src/modules/about-us/components/shared/AboutUsHeading';
import { Theme } from 'vendor/mods/styled-components';
import { redTheme } from 'src/styles/theme';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import mapImage from 'src/modules/about-us/assets/facilities/map.png';
import matexlabImage from 'src/modules/about-us/assets/facilities/matexlab.png';
import bergerAndCraftImage from 'src/modules/about-us/assets/facilities/berger-and-kraft.png';
import lemImage from 'src/modules/about-us/assets/facilities/lem.png';

const Container = styled(AboutUsSection)`
  max-width: 100%;
  padding: 0 0 60px;
  background: ${({ theme }) => theme.palette.background};

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 90px 0;
  }
`;

const ContentContainer = styled(ConstraintContainer)`
  display: flex;
  flex-direction: column;
  gap: 50px;
  padding: 0;
  ${media.w.greaterThanOrEqual.desktop} {
    gap: 80px;
    flex-direction: row;
    align-items: center;
    & > ${ConstraintContainer} {
      max-width: 420px;
      padding-left: 0;
    }
  }
`;

const StyledHeading = styled(AboutUsHeading)`
  color: ${({ theme }) => theme.text.palette.primary};
`;

const Description = styled.div`
  margin: 16px 0;

  ${media.w.greaterThanOrEqual.desktop} {
    margin: 28px 0;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 323px;

  ${media.w.greaterThanOrEqual.desktop} {
    width: 764px;
    height: 659px;
  }
`;

const Facilities = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  ${media.w.lessThan.desktop} {
    margin-top: 40px;
  }
`;

const Facility = styled.div`
  display: flex;
  flex-direction: column-reverse;
  ${media.w.greaterThanOrEqual.desktop} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const FacilityContent = styled.div`
  width: 75%;
  font-size: 12px;
  line-height: 18px;
`;

const Name = styled.h3`
  font-size: 16px;
  line-height: 21px;
  font-weight: 500;

  margin-bottom: 8px;
  ${media.w.lessThan.desktop} {
    margin-top: 18px;
  }
`;

const FacilityImage = styled.div`
  position: relative;
  width: 120px;
  height: 70px;
`;

const facilities = [
  {
    title: 'NEAUVIA PEG-HA FILLERS*',
    text: 'VIA CARLO URBANI',
    image: matexlabImage,
  },
  {
    title: 'NEAUVIA ENERGY BASED DEVICES**',
    text: 'Berger&Kraft',
    image: bergerAndCraftImage,
  },
  {
    title: 'NEAUVIA AESTHETIC COSMECEUTICALS***',
    text: 'Via Oberdan',
    image: lemImage,
  },
] as const;

export const AboutUsFacilities: FC = () => {
  const { _ } = useTranslate();

  return (
    <Theme theme={redTheme}>
      <Container>
        <ContentContainer>
          <ImageContainer>
            <Image
              src={mapImage}
              quality={90}
              objectFit="contain"
              layout="fill"
            />
          </ImageContainer>
          <ConstraintContainer>
            <StyledHeading>
              <Markdown>{_('Neauvia Facilities')}</Markdown>
            </StyledHeading>
            <Description>
              <Markdown>
                {_(
                  'NEAUVIA company is part of MATEX LAB Group and has production sites in Italy and Poland, mastering advanced in-house technologies and the production of its whole portfolio.',
                )}
              </Markdown>
            </Description>
            <Facilities>
              {facilities.map(({ title, text, image }) => (
                <Facility key={title}>
                  <FacilityContent>
                    <Name>{_(title)}</Name>
                    <Markdown>{_(text)}</Markdown>
                  </FacilityContent>
                  <FacilityImage>
                    <Image
                      src={image}
                      quality={90}
                      layout="fill"
                      objectFit="contain"
                      objectPosition="0 50%"
                    />
                  </FacilityImage>
                </Facility>
              ))}
            </Facilities>
          </ConstraintContainer>
        </ContentContainer>
      </Container>
    </Theme>
  );
};
